// src/components/PaymentMethods.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaTrash, FaTimesCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPaymentMethods,
  deletePaymentMethod,
  setDefaultPaymentMethod
} from '../../store/reducers/paymentReducer';
import type { RootState, AppDispatch } from '../../store/store';
import './PaymentMethods.css';

const PaymentMethods: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    cardList,
    isFetching,
    fetchError,
    isDeleting,
    deleteError,
    isSettingDefault,
    setDefaultError
  } = useSelector((state: RootState) => state.payment);

  const [selectedId, setSelectedId] = useState<string>('');

  useEffect(() => {
    dispatch(fetchPaymentMethods());
  }, [dispatch]);

  const handleSetDefault = () => {
    dispatch(setDefaultPaymentMethod(selectedId));
    };

  const handleDelete = (id: string) => {
    if (selectedId === id) {
      setSelectedId('');
    }
    dispatch(deletePaymentMethod(id));
  };

  return (
    <div className="pm-container">
      <div className="pm-card">
        <header className="pm-header">
          <div className="pm-header-left">
            <FaArrowLeft
              className="pm-back-icon"
              onClick={() => navigate(-1)}
            />
            <h2 className="pm-title">Payment Methods</h2>
          </div>
          <button
            className="pm-add-card"
            onClick={() => navigate('/Add-Card')}
          >
            <FaPlus className="pm-add-icon" />
            Add Card
          </button>
        </header>

        <div className="pm-body">
          {isFetching && <div className="pm-loading">Loading…</div>}
          {!isFetching && fetchError && (
            <div className="pm-error">
              <span className="pm-error-icon"><FaTimesCircle /></span>
              Error: {fetchError}
            </div>
          )}
          {!isFetching && !fetchError && cardList.length === 0 && (
            <div className="pm-empty">No Card Added yet</div>
          )}
          {!isFetching && !fetchError && cardList.length > 0 && (
            <ul className="pm-list">
              {cardList.map((card) => (
                <li key={card.id} className="pm-card-item">
                  <div className="pm-card-details">
                    <div className="pm-card-type">
                      <span>{card.type.toUpperCase()}</span>
                      {card.isDefault && (
                        <span className="default-badge" aria-label="Default card">Default</span>
                      )}
                    </div>
                    <div className="pm-card-number">•••• {card.last4}</div>
                    <div className="pm-card-exp">
                      Expires{' '}
                      {String(card.expMonth).padStart(2, '0')}/
                      {card.expYear}
                    </div>
                  </div>

                  <div className="pm-item-actions">
                    <label className="pm-checkbox-label" aria-label="Select card for default">
                      <input
                        type="checkbox"
                        checked={selectedId === card.id}
                        onChange={() =>
                          setSelectedId(
                            selectedId === card.id ? '' : card.id
                          )
                        }
                      />
                    </label>

                    <button
                      className="pm-delete-btn"
                      onClick={() => handleDelete(card.id)}
                      aria-label="Delete card"
                      disabled={isDeleting}
                    >
                      <FaTrash size={20} style={{ color: 'inherit' }} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {deleteError && (
            <div className="pm-error">Delete failed: {deleteError}</div>
          )}
        </div>

        <div className="pm-footer">
          <button
            className="pm-button"
            onClick={handleSetDefault}
            disabled={!selectedId || isSettingDefault}
          >
            {isSettingDefault ? 'Setting…' : 'Set as Default'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethods;
